import React, { Component } from 'react'
import { Trans, withTranslation } from 'react-i18next'
import './styles.scss'
import ImageBrowserItem from './Item'
import Lightbox from '../../../Shared/Lightbox'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import Alert from '../../../Shared/Alert'
import UploadZone from '../../../Shared/UploadZone'
import UploadItem from './uploadItem'
import PropTypes from 'prop-types'
import { arrayMoveImmutable } from 'array-move'
import { isImageConfirmed } from './utils'
import activityTrackingService from '../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../constants'
import LayerConfirmImages from './Layer/LayerConfirmImages'
import PageHead from '../../../Shared/PageHead'
import Button from '../../../Shared/Form/Button'
import PublishAllButton from '../PublishAllButton'
import { ReactComponent as SaveIcon } from '../../../../assets/img/icons/save.svg'
import { BUTTON_MIN_WIDTH } from '../../../Shared/Form/Button/constants'
import InfoBar from '../../../Shared/InfoBar'
import { Link } from 'react-router-dom'
import ActionBar from './ActionBar'
import { deleteImages } from '../../../../api'
import DeleteConfirmationOverlay from '../../../Shared/DeleteConfirmationOverlay'
import { GALLERIES_SHAPE } from '../shapes'
import classnames from 'classnames'
import withToast from '../../../Shared/Toast/withToast'
import { isUploadQueueActive } from '../helpers'

const getOrderedImages = (images, order) => {
  if (!order) {
    return images
  }
  const imageMap = new Map()
  images.forEach(image => {
    imageMap.set(image.id, image)
  })

  const result = []

  order.forEach(imageId => {
    const image = imageMap.get(imageId)
    if (image) {
      result.push(image)
    }
  })

  return result
}

const SortableImageBrowserItem = SortableElement(({
                                                    sortIndex,
                                                    image,
                                                    rooms,
                                                    roomAttributes,
                                                    onZoomClick,
                                                    onImageChanged,
                                                    isGiataUser,
                                                    permissions,
                                                    isSelected,
                                                    onImageSelectChange,
                                                    selectedImagesCount,
                                                    galleries = {},
                                                    onGalleriesChange,
                                                    onDeleteRequest,
                                                    syncingImageId,
                                                  }) => (
  <ImageBrowserItem key={image.id + '_ibi'} number={sortIndex} rooms={rooms}
                    roomAttributes={roomAttributes} image={image}
                    onZoomClick={onZoomClick}
                    onImageChanged={onImageChanged} isGiataUser={isGiataUser}
                    permissions={permissions} isSelected={isSelected}
                    onImageSelectChange={onImageSelectChange}
                    selectedImagesCount={selectedImagesCount}
                    galleries={galleries}
                    onGalleriesChange={onGalleriesChange}
                    onDeleteRequest={onDeleteRequest}
                    isSyncing={syncingImageId === image.id}
  />
))

const SortableImageBrowserItems = SortableContainer(({
                                                       images,
                                                       uploadQueue,
                                                       rooms,
                                                       roomAttributes,
                                                       onZoomClick,
                                                       onUploadCancel,
                                                       onUploadQueueImageRemove,
                                                       onImageChanged,
                                                       openDropzoneDialog,
                                                       isGiataUser,
                                                       permissions,
                                                       selectedImageIdsMap,
                                                       onImageSelectChange,
                                                       galleries = {},
                                                       onGalleriesChange,
                                                       onDeleteRequest,
                                                       syncingImageId,
                                                     }) => {
  const selectedImagesCount = Object.keys(selectedImageIdsMap).length

  return (
    <div className="Imagebrowser-Items clearfix">
      <div className="Imagebrowseritem Imagebrowseritem-Add" onClick={() => openDropzoneDialog()}>
        <div>
          <p className="UploadZone-Add">
            <Trans>LABEL_ADD_IMAGE</Trans></p>
        </div>
        <span><Trans>HINT_TEXT_ADD_IMAGE_WITH_DRAG</Trans></span>
      </div>
      {images.map((image, index) => {
        return (
          <SortableImageBrowserItem key={image.id + '_SortableItem'} index={index} sortIndex={index} image={image}
                                    rooms={rooms} roomAttributes={roomAttributes}
                                    onZoomClick={onZoomClick} onImageChanged={onImageChanged}
                                    isGiataUser={isGiataUser}
                                    permissions={permissions}
                                    isSelected={selectedImageIdsMap.hasOwnProperty(image.id)}
                                    onImageSelectChange={onImageSelectChange}
                                    selectedImagesCount={selectedImagesCount}
                                    galleries={galleries}
                                    onGalleriesChange={onGalleriesChange}
                                    disabled={selectedImagesCount > 0}
                                    onDeleteRequest={onDeleteRequest}
                                    syncingImageId={syncingImageId}
          />
        )
      })}
      {uploadQueue.map((image, index) => {
        return (
          <UploadItem image={image} key={index + '_imageToUpload'} onUploadCancel={onUploadCancel}
                      onRemove={onUploadQueueImageRemove}/>
        )
      })}
    </div>
  )
})

const isGiataMailRegEx = /^.*@(giata\.de|giata\.com)$/i

class ImageBrowser extends Component {

  constructor (props) {
    super(props)
    this.state = {
      activeImage: 0,
      error: null,
      isSyncing: false,
      syncingImageId: null,
      layerConfirmSave: false,
      optimisticOrder: null,
      showDeleteConfirmation: false,
      imageIdsToDelete: [],
      deleteQuestion: '',
      isUploadInProgress: false,
    }
    this.dropzoneRef = React.createRef()
    this.lightboxRef = React.createRef()
    this.sortStart = null
  }

  componentDidMount () {
    activityTrackingService.trackPageView('Upload Images', {
      giataId: this.props.property.giataId,
    })
  }

  componentDidUpdate (prevProps, prevState, snapshot) {
    if (prevProps.galleries.property.order !== this.props.galleries.property.order) {
      if (this.state.optimisticOrder !== null) {
        this.setState({ optimisticOrder: null })
      }
    }
    if (prevProps.uploadQueue !== this.props.uploadQueue) {
      if (isUploadQueueActive(this.props.uploadQueue)) {
        this.setState({ isUploadInProgress: true })
      } else {
        this.setState({ isUploadInProgress: false })
      }
    }
  }

  onUploadDrop = (files) => {
    activityTrackingService.trackEvent(trackingEvent.uploadImages, {
      giataId: this.props.property.giataId,
      numberOfImages: files.length,
      page: 'Upload Images'
    })
    this.props.onUploadFiles(files)
  }

  onSortStart = ({ index }, event) => {
    this.sortStart = {
      time: Date.now(),
      x: event.clientX ?? event.touches?.[0]?.clientX ?? 0,
      y: event.clientY ?? event.touches?.[0]?.clientY ?? 0,
      index,
    }
  }

  onSortEnd = ({ oldIndex, newIndex }, event) => {
    const start = this.sortStart
    this.sortStart = null

    if (start) {
      // check if the click was a click and not a drag
      const CLICK_MAX_DURATION_MS = 200
      const CLICK_MAX_MOVEMENT_PX = 5
      const elapsed = Date.now() - start.time
      const endX = event.clientX ?? event.changedTouches?.[0]?.clientX ?? start.x
      const endY = event.clientY ?? event.changedTouches?.[0]?.clientY ?? start.y
      const moved = Math.hypot(endX - start.x, endY - start.y)

      if (elapsed < CLICK_MAX_DURATION_MS && moved < CLICK_MAX_MOVEMENT_PX) {
        // Treat as a click rather than a drag. This is the single source of truth for
        // click-to-zoom; while a multi-select is active the click toggles selection
        // (handled by the item) instead of opening the lightbox.
        if (Object.keys(this.props.selectedImageIdsMap).length === 0) {
          this.onZoomClick(oldIndex)
        }
        return
      }
    }

    this.saveImageSequence(oldIndex, newIndex)
  }

  onImageChanged = (newImage) => {
    let index = this.props.images.findIndex(i => i.id === newImage.id)
    if (index > -1) {
      const newImages = [...this.props.images]

      newImages[index] = newImage
      this.props.onImagesChange(newImages)
    }
  }

  saveImageSequence = (oldIndex, newIndex) => {
    if (oldIndex === newIndex || this.state.isSyncing) {
      return
    }
    activityTrackingService.trackEvent(trackingEvent.reorderImage)

    const currentOrder = this.state.optimisticOrder || this.props.galleries.property.order
    const orderedImages = getOrderedImages(this.props.images, currentOrder)
    const newlyOrderedImages = arrayMoveImmutable(orderedImages, oldIndex, newIndex)
    const newPropertyImagesOrder = newlyOrderedImages.map(image => image.id)

    this.setState({
      optimisticOrder: newPropertyImagesOrder,
      isSyncing: true,
      syncingImageId: orderedImages[oldIndex].id,
    })

    this.props.onGalleriesChange({ property: { order: newPropertyImagesOrder } })
      .then(() => {
        this.setState({ optimisticOrder: null, isSyncing: false, syncingImageId: null })
      })
      .catch(() => {
        this.setState({
          optimisticOrder: null,
          isSyncing: false,
          syncingImageId: null,
          error: this.props.t('images.errors.reOrder'),
        })
      })
  }

  onSaveClick = () => {
    activityTrackingService.trackEvent(trackingEvent.triggerImageConfirmationPrompt)
    this.setState({ layerConfirmSave: true })
  }

  onUploadQueueImageRemove = (image) => {
    const index = this.props.uploadQueue.indexOf(image)
    if (index > -1) {
      let uploadQueueNew = [...this.props.uploadQueue]
      uploadQueueNew.splice(index, 1)
      this.props.onUploadQueueChange(uploadQueueNew)
    }
  }

  onImageSelectChange = (imageId, selected) => {
    const newSelectedImageIdsMap = Object.assign({}, this.props.selectedImageIdsMap)
    if (!selected) {
      delete newSelectedImageIdsMap[imageId]
    } else {
      newSelectedImageIdsMap[imageId] = true
    }
    this.props.onSelectedImageIdsMapChange(newSelectedImageIdsMap)
  }

  openDropzoneDialog = () => {
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open && this.dropzoneRef.current.open()
    }
  }
  onImagesConfirmed = () => {
    const newImages = []
    this.props.images.forEach((image) => {
      image.confirmed = true
      newImages.push(image)
    })
    this.props.onImagesChange(newImages)
    window.scrollTo({ top: 0 })
  }

  onZoomClick = (index) => {
    this.setState({
      activeImage: index
    })

    setTimeout(() => {
      if (this.lightboxRef.current) {
        this.lightboxRef.current.openLightbox()
      }
    }, 300)
  }
  onCancelClick = () => {
    this.props.onSelectedImageIdsMapChange({})
  }

  handleDeleteRequestForSelection = (imageIds) => {
    this.setState({
      showDeleteConfirmation: true,
      imageIdsToDelete: imageIds,
      deleteQuestion: this.props.t('images.deleteConfirmationDialog.deleteQuestion', { count: imageIds.length }),
    })
  }

  handleDeleteRequestForSpecificImage = (imageIds) => {
    this.setState({
      showDeleteConfirmation: true,
      imageIdsToDelete: imageIds,
      deleteQuestion: this.props.t('images.deleteConfirmationDialog.deleteQuestionSpecificImage'),
    })
  }

  handleDeleteCancel = () => {
    this.setState({
      showDeleteConfirmation: false,
      imageIdsToDelete: [],
    })
  }

  handleDeleteConfirm = () => {
    const { imageIdsToDelete } = this.state
    activityTrackingService.trackEvent(trackingEvent.deleteImages, {
      numberOfImages: imageIdsToDelete.length,
    })
    const { fetchPromise } = deleteImages(this.props.property.giataId, imageIdsToDelete)
    this.setState({ isSyncing: true })
    return fetchPromise.then(() => {
      this.props.onImagesDeleted(imageIdsToDelete)
      this.handleDeleteCancel()
    }).catch(() => {
      this.setState({ error: this.props.t('images.errors.deleteImages') })
      this.handleDeleteCancel()
    }).finally(() => {
      this.setState({ isSyncing: false })
    })
  }

  handleHideFromPropertyGalleryRequest = () => {
    let newHero = this.props.galleries.property.heroImage
    if (newHero && this.props.selectedImageIdsMap.hasOwnProperty(newHero)) {
      newHero = null
    }
    const selectionCount = Object.keys(this.props.selectedImageIdsMap).length
    const patch = {
      property: {
        order: this.props.galleries.property.order
          .filter(imageId => !this.props.selectedImageIdsMap.hasOwnProperty(imageId)),
        heroImage: newHero,
      }
    }
    this.props.onSelectedImageIdsMapChange({})
    this.setState({ isSyncing: true })
    this.props.onGalleriesChange(patch)
      .then(() => {
        this.setState({ isSyncing: false })
        this.props.toast.success(
          <Trans
            i18nKey="images.toasts.removeFromPropertyGallerySuccess"
            count={selectionCount}
          />,
        )
      })
      .catch(() => {
        this.setState({ isSyncing: false, error: this.props.t('images.errors.hideFromPropertyGallery') })
      })
  }

  render () {
    const isGiataUser = isGiataMailRegEx.test(this.props.user.user)
    const unconfirmedImages = this.props.images.filter(image => !isImageConfirmed(image)).length
    const { t } = this.props
    const saveDisabled = !this.props.showSaveButton || unconfirmedImages === 0
    const order = this.state.optimisticOrder || this.props.galleries.property.order
    const imagesOrdered = getOrderedImages(this.props.images, order)
    const selectedImageIds = Object.keys(this.props.selectedImageIdsMap)
    const isImageOpsDisabled = this.state.isSyncing || this.state.isUploadInProgress
    const imageBrowserClasses = classnames('ImageBrowser', {
      'syncing': this.state.isSyncing,
      'image-ops-disabled': isImageOpsDisabled
    })

    return (
      <div className="page" id={'image-browser-page'}>
        {this.state.error && <Alert type={'danger'}>{this.state.error}</Alert>}
        {!this.state.error &&
          <React.Fragment>
            <PageHead
              title={t('images.header.title')}
              subtitle={t('images.header.subtitle')}
            >
              <Button
                notificationBadge={unconfirmedImages > 0 ? unconfirmedImages : undefined}
                disabled={saveDisabled}
                onClick={this.onSaveClick}
                variant="secondary"
                minWidth={BUTTON_MIN_WIDTH}
                icon={<SaveIcon/>}
              >
                {this.props.t('CONFIRM_IMAGES')}
              </Button>
              <PublishAllButton giataId={this.props.property.giataId}/>
            </PageHead>
            {
              selectedImageIds.length < 1
                ? <InfoBar infoId="info-reorder-gallery">
                  <Trans
                    i18nKey="images.infoBar.reorderGallery"
                    components={{
                      strong: <strong/>,
                      editRoomsLink: <Link
                        onClick={() => activityTrackingService.trackEvent(trackingEvent.clickEditRoomsInImageBrowser)}
                        to={`/property/${this.props.property.giataId}/edit-rooms`}
                      />,
                    }}
                  />
                </InfoBar>
                : <ActionBar
                  giataId={this.props.property.giataId}
                  galleries={this.props.galleries}
                  selectedImageIds={selectedImageIds}
                  onCancelClick={this.onCancelClick}
                  onDeleteRequest={this.handleDeleteRequestForSelection}
                  onHideFromPropertyGalleryRequest={this.handleHideFromPropertyGalleryRequest}
                />
            }
            <Lightbox images={imagesOrdered} index={this.state.activeImage} ref={this.lightboxRef}/>
            <UploadZone disabled={isImageOpsDisabled} onDrop={this.onUploadDrop} dropzoneRef={this.dropzoneRef}>
              <div className={imageBrowserClasses} id="ImageBrowser">
                <div style={{ padding: 0 }} className="container clearfix">
                  <SortableImageBrowserItems axis="xy" useDragHandle={true}
                                             helperContainer={document.getElementById('ImageBrowser')}
                                             onSortStart={this.onSortStart}
                                             onSortEnd={this.onSortEnd} images={imagesOrdered}
                                             uploadQueue={this.props.uploadQueue}
                                             rooms={this.props.rooms}
                                             roomAttributes={this.props.roomAttributes}
                                             onZoomClick={this.onZoomClick}
                                             onUploadCancel={this.onUploadCancel}
                                             onImageChanged={this.onImageChanged}
                                             onUploadQueueImageRemove={this.onUploadQueueImageRemove}
                                             openDropzoneDialog={this.openDropzoneDialog}
                                             useWindowAsScrollContainer={true}
                                             permissions={this.props.permissions}
                                             isGiataUser={isGiataUser}
                                             selectedImageIdsMap={this.props.selectedImageIdsMap}
                                             onImageSelectChange={this.onImageSelectChange}
                                             galleries={this.props.galleries}
                                             onGalleriesChange={this.props.onGalleriesChange}
                                             onDeleteRequest={this.handleDeleteRequestForSpecificImage}
                                             syncingImageId={this.state.syncingImageId}
                                             shouldCancelStart={() => isImageOpsDisabled}
                  />
                </div>
              </div>
            </UploadZone>
            {this.props.showSaveButton &&
              <React.Fragment>
                <LayerConfirmImages
                  images={this.props.images} onConfirmed={this.onImagesConfirmed}
                  user={this.props.user.user} show={this.state.layerConfirmSave}
                  onHideClick={() => this.setState({ layerConfirmSave: false })}
                />
              </React.Fragment>
            }
            {this.state.showDeleteConfirmation && <DeleteConfirmationOverlay
              question={this.state.deleteQuestion}
              information={t('images.deleteConfirmationDialog.imageDeletionInfo')}
              onCancel={this.handleDeleteCancel}
              onConfirmation={this.handleDeleteConfirm}
            />}
          </React.Fragment>

        }
      </div>
    )
  }
}

ImageBrowser.propTypes = {
  property: PropTypes.object.isRequired,
  images: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  roomAttributes: PropTypes.object.isRequired,
  showSaveButton: PropTypes.bool.isRequired,
  permissions: PropTypes.object.isRequired,
  uploadQueue: PropTypes.array.isRequired,
  onImagesChange: PropTypes.func.isRequired,
  onUploadQueueChange: PropTypes.func.isRequired,
  onUploadFiles: PropTypes.func.isRequired,
  selectedImageIdsMap: PropTypes.object.isRequired,
  onSelectedImageIdsMapChange: PropTypes.func.isRequired,
  galleries: GALLERIES_SHAPE.isRequired,
  onGalleriesChange: PropTypes.func.isRequired,
  onImagesDeleted: PropTypes.func.isRequired,
}
export default withTranslation()(withToast(ImageBrowser))
